import angular from 'angular';
import _ from 'lodash-es';
import APIV5 from '~/models/api';
import kformUtils from '~/components/kform/utils/kform-utils';
import Form from '~/scripts/services/model/form';
import Node from '~/scripts/services/model/node';

export default class KformBuilderController {
	/* @ngInject */
	/**
	 * @param {import('angular').auto.IInjectorService} $injector
	 * @param {import('angular').IIntervalService} $interval
	 * @param {import('angular').IQService} $q
	 * @param {any} $scope
	 * @param {import('angular').ITimeoutService} $timeout
	 * @param {any} KformComponents
	 * @param {any} debounce
	 * @param {any} dndDragIframeWorkaround
	 */
	constructor(
		$injector,
		$interval,
		$q,
		$scope,
		$timeout,
		KformComponents,
		debounce,
		dndDragIframeWorkaround,
	) {
		this.$injector = $injector;
		this.debounce = debounce;
		this.$scope = $scope;
		this.KformComponents = KformComponents;
		this.dndDragIframeWorkaround = dndDragIframeWorkaround;
		this.$interval = $interval;
		this.$q = $q;
		this.$timeout = $timeout;

		this.expanded = false;
		this.hideCount = 2;

		/** @type {Node | undefined} */
		this.node = undefined;

		this.options = {};
		this.fields = undefined;
		this.form = undefined;

		/** @type {any} */
		this.formComponents = {};
		this.formComponentsByGroup = {};
	}

	/**
	 * @param {any} $scope
	 * @param {any} element
	 */
	link($scope, element) {
		const minHeight = 200;
		const headerOffset = 50;
		const bottomOffset = 15;

		const scrollSidebar = () => {
			// Disable all buttons within the form.
			angular.element('.formbuilder').find('button').attr('disabled', 'disabled');

			// Make the left column follow the form.
			const formComponents = angular.element('.formcomponents');
			const formBuilder = angular.element('.formbuilder');
			if (formComponents.length > 0 && formBuilder.length > 0) {
				const windowElement = angular.element(window);
				const windowHeight = windowElement.height() || 0;
				const windowScrollTop = windowElement.scrollTop() || 0;
				const windowScrollBottom = (windowScrollTop || 0) + (windowHeight || 0);

				const formBuilderOffsetTop = formBuilder.offset()?.top || 0;
				const formBuilderHeight = formBuilder.outerHeight() || 0;
				const formBuilderOffsetBottom = formBuilderOffsetTop + formBuilderHeight;

				let height = 0;

				if (windowHeight > formBuilderHeight) {
					formComponents.css('height', formBuilderHeight);
					return;
				}

				if (windowScrollBottom < formBuilderOffsetTop
					|| windowScrollTop > formBuilderOffsetBottom) {
					// Form Builder is not visible.
					return;
				}
				if (windowScrollTop < formBuilderOffsetTop) {
					// Top part of Form Builder is visible.
					height = windowScrollBottom - formBuilderOffsetTop - bottomOffset;
				}
				else if (windowScrollBottom < formBuilderOffsetBottom) {
					// Form builder is visible.
					height = windowHeight - headerOffset - bottomOffset;
				}
				else {
					// Bottom part of Form Builder is visible.
					height = formBuilderOffsetBottom - windowScrollTop - headerOffset - bottomOffset;
				}

				if (height < minHeight) {
					height = minHeight;
				}

				const maxScroll = formBuilderHeight - height - bottomOffset;
				let scroll = windowScrollTop - formBuilderOffsetTop + headerOffset;
				if (scroll < 0) {
					scroll = 0;
				}
				if (scroll > maxScroll) {
					scroll = maxScroll;
				}

				// Necessary fix for header.
				if (scroll > 0 && scroll < headerOffset) {
					height -= scroll;
				}

				formComponents.css({
					'margin-top': scroll,
					height,
				});
			}
		};

		const debounceScrollBar = this.debounce(scrollSidebar, 100, false);
		window.addEventListener('scroll', debounceScrollBar);
		scrollSidebar();
		element.on('$destroy', () => {
			window.removeEventListener('scroll', debounceScrollBar);
		});
	}

	/**
	 * @param {import('angular').IOnChangesObject} changes
	 */
	$onChanges(changes) {
		if (changes.node && this.node instanceof Node) {
			if (_.isEqual({}, this.node) || this.node === undefined) {
				return;
			}
			this.$timeout(() => {
				if (this.formComponentGroups?.node) {
					this.formComponentGroups.node.kid = this.node?.kid;
					this.formComponentGroups.node.title = this.node?.getName();
				}
			});
			this.fields = {
				down: [],
				up:   [],
			};

			const componentExpand = async (/** @type {any} */ component) => {
				component.expanded = !component.expanded;

				// @ts-ignore
				if (component.expanded && Object.keys(component.components).length === 0
					&& component.settings && component.settings.kdata) {
					// @ts-ignore
					const rootPath = angular.copy(component.settings.kdata.path);
					rootPath.pop();
					// @ts-ignore
					/** @type {any} */
					const components = {};

					if (component.settings.kdata.kid) {
						const result = await APIV5.instance.builderChildren(
							{
								nodeid: component.settings.kdata.kid,
								root_path: rootPath,
							},
							{ id: 'kform-builder.component-expand' },
						);
						let oldFlow = 'down';

						for (const link of result.result) {
							if ('formio_component' in link && link.formio_component.type in this.formComponents) {
								const id = link.formio_component.key;
								components[id] = { ...this.formComponents[link.formio_component.type] };
								components[id].children = link.children === 1;
								components[id].components = {};
								components[id].expand = async () => {
									// eslint-disable-next-line promise/no-nesting
									const cs = await componentExpand(components[id]);
									components[id].components = cs;
									return cs;
								};
								components[id].expanded = false;
								components[id].flow = link.flow;
								components[id].group = 'node';
								components[id].selectable = link.selectable === 1;
								components[id].settings = link.formio_component;
								if (components[id].settings.kdata && !components[id].settings.kdata.kid && link.kid) {
									components[id].settings.kdata.kid = link.kid;
								}

								if (components[id].settings.kdata?.path) {
									components[id].settings.disabled = components[id].settings.kdata.path.length > 2;
								}
								else if ('components' in components[id]) {
									for (const c of Object.values(components[id].components)) {
										if (c.settings.kdata?.path) {
											c.settings.disabled = c.settings.kdata.path.length > 2;
										}
									}
								}
								components[id].settings.isNew = false;
								// components[id].settings.kdata.path = path;
								if ('label' in link.formio_component) {
									components[id].title = link.formio_component.label;
								}

								if (oldFlow === link.flow) {
									components[id].sep = false;
								}
								else {
									components[id].sep = true;
									oldFlow = link.flow;
								}
							}
						}
					}

					this.$timeout(() => {
						this.$scope.$apply();
					});

					return components;
				}
				return false;
			};

			(async () => {
				/** @namespace node.objid */
				const rootComponent = {
					settings: { kdata: { kid: this.node?.kid, path: [{ kid: this.node?.kid, flow: null }] } },
					components: {},
				};
				const components = await componentExpand(rootComponent);
				angular.extend(this.formComponents, components);
				// @ts-ignore
				this.formComponentsByGroup = _.groupBy(this.formComponents, component => component.group);
			})();
		}
	}

	$onInit() {
		// Add the components to the scope.
		if (!this.form) {
			this.form = new Form(this.$injector);
		}

		this.$scope.$on('iframe-componentUpdate', (/** @type {Event} */ event, /** @type {any} */ data) => {
			kformUtils.eachComponent(this.form?.components, (/** @type {any} */ component) => {
				if (data.id && component.id) {
					if (component.id === data.id) {
						component.overlay = data.overlay;
					}
				}
				else if (component.key === data.key) {
					component.overlay = data.overlay;
				}
			});
		});

		this.$scope.$on('iframe-componentClick', (/** @type {Event} */ event, /** @type {any} */ data) => {
			kformUtils.eachComponent(this.form?.components, (/** @type {any} */ component) => {
				if (data.id && component.id) {
					if (component.id === data.id) {
						this.$scope.$broadcast('editComponent', component);
					}
				}
				else if (component.key === data.key) {
					this.$scope.$broadcast('editComponent', component);
				}
			});
		});

		this.formComponents = _.cloneDeep(this.KformComponents.components);
		for (const [key, component] of Object.entries(this.formComponents)) {
			component.settings.isNew = true;
			if (('builder' in component.settings && !component.settings.builder) || component.disabled) {
				delete this.formComponents[key];
			}
		}

		this.formComponentGroups = _.cloneDeep(_.omitBy(this.KformComponents.groups, 'disabled'));
		// @ts-ignore
		this.formComponentsByGroup = _.groupBy(this.formComponents, component => component.group);

		// const update = () => {
		// 	this.$scope.$emit('formUpdate', this.form);
		// };
		//
		// // Add a new component.
		// this.$scope.$on('formBuilder:add', update);
		// this.$scope.$on('formBuilder:update', update);
		// this.$scope.$on('formBuilder:remove', update);
		// this.$scope.$on('formBuilder:edit', update);

		this.$scope.capitalize = _.capitalize;

		// Set the root list height to the height of the formbuilder for ease of form building.
		const rootlistEL = angular.element('.rootlist');
		const formbuilderEL = angular.element('.formbuilder');

		this.$interval(() => {
			const listHeight = rootlistEL.height('inherit').height() || 0;
			const builderHeight = formbuilderEL.height() || 0;
			if ((builderHeight - listHeight) > 100) {
				rootlistEL.height(builderHeight);
			}
		}, 1000);
	}
}
